<template>
  <div class="app-nav-root">
    <header class="app-nav" :class="{ 'nav-scrolled': isScrolled }">
      <v-container>
        <div class="nav-content">
          <div class="nav-brand" @click="navigateTo('/')">
            <div class="nav-logo-icon">
              <v-icon size="20" color="#1E3733">mdi-heart</v-icon>
            </div>
            <div class="nav-logo-text">
              <div class="nav-heartline">Heartline</div>
              <div class="nav-hub">HUB</div>
            </div>
          </div>

          <!-- Home/Why/Features/About/Login are live routes right now.
               Blog, Documentation, Help Center, Contact, Security, and
               Demo point at their real intended paths even though those
               routes aren't registered yet — see router/index.js's
               commented-out block for the matching entries to uncomment
               once each view file exists. A router-link to an unregistered
               path won't break the build (unlike an unresolved import in
               the router file); it just won't navigate anywhere useful
               until the route catches up. -->
          <nav class="nav-links">
            <router-link to="/" class="nav-link" exact>Home</router-link>
            <router-link to="/why" class="nav-link">Why Heartline Hub</router-link>
            <router-link to="/features" class="nav-link">Features</router-link>
            <router-link to="/tribzz" class="nav-link">Tribzz</router-link>
            <router-link to="/about" class="nav-link">About</router-link>
            <router-link to="/blog" class="nav-link">Blog</router-link>
          </nav>

          <div class="nav-actions">
            <v-btn outlined class="nav-btn-login" to="/login">Login</v-btn>
            <v-btn depressed class="nav-btn-demo" to="/demo">Request Demo</v-btn>
          </div>

          <button class="nav-burger" @click="drawer = true">
            <v-icon color="#FBF7EF">mdi-menu</v-icon>
          </button>
        </div>
      </v-container>
    </header>

    <!-- Mobile Drawer — deliberately a SIBLING of <header>, not nested inside
         it. .app-nav uses backdrop-filter for its frosted-glass effect, and
         backdrop-filter (like filter/transform) creates a new containing
         block for any position:fixed descendants — so if this drawer lived
         inside <header>, its "fixed" positioning would be scoped to the
         header's own ~70px height instead of the full viewport, clipping
         the overlay and nav links off-screen. Keeping it as a sibling avoids
         that entirely. (Carried over from the Corporate Connectionz nav
         this was adapted from — same underlying markup, real fix either way.) -->
    <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
      <div class="drawer-panel" @click.stop>
        <div class="drawer-header">
          <div class="drawer-logo">
            <v-icon size="20" color="#1E3733">mdi-heart</v-icon>
          </div>
          <div class="drawer-brand">
            <div>Heartline</div>
            <div class="brand-sub">HUB</div>
          </div>
          <button class="drawer-close" @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <v-divider class="my-4"></v-divider>
        <div class="drawer-nav">
          <a class="drawer-link" @click="navigateTo('/')">
            <v-icon left>mdi-home</v-icon> Home
          </a>
          <a class="drawer-link" @click="navigateTo('/why')">
            <v-icon left>mdi-star-four-points</v-icon> Why Heartline Hub
          </a>
          <a class="drawer-link" @click="navigateTo('/features')">
            <v-icon left>mdi-view-grid</v-icon> Features
          </a>
          <a class="drawer-link" @click="navigateTo('/tribzz')">
            <v-icon left>mdi-account-group</v-icon> Tribzz for Communities
          </a>
          <a class="drawer-link" @click="navigateTo('/about')">
            <v-icon left>mdi-information</v-icon> About
          </a>
          <a class="drawer-link" @click="navigateTo('/blog')">
            <v-icon left>mdi-post</v-icon> Blog
          </a>
          <v-divider class="my-4"></v-divider>
          <a class="drawer-link" @click="navigateTo('/login')">
            <v-icon left>mdi-login</v-icon> Login
          </a>
          <a class="drawer-link drawer-link-gold" @click="navigateTo('/demo')">
            <v-icon left>mdi-calendar-check</v-icon> Request Demo
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppNavigation',
  data() {
    return {
      drawer: false,
      isScrolled: false
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    navigateTo(route) {
      this.drawer = false;
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

.app-nav-root {
  display: contents;
}

.app-nav {
  --rc-teal-deep: #1E3733;
  --rc-teal: #2F4F4A;
  --rc-gold: #C89B4C;
  --rc-gold-soft: #E4C989;
  --rc-cream: #FBF7EF;
  --rc-ink: #26302C;
  --rc-ink-soft: #5B6663;
  --rc-line: #E4DCC9;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(30, 55, 51, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(251, 247, 239, 0.1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.app-nav.nav-scrolled {
  background: rgba(20, 38, 35, 0.97);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.app-nav ::v-deep .v-btn {
  text-transform: none !important;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.nav-logo-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(150deg, var(--rc-gold-soft) 0%, var(--rc-gold) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.nav-heartline {
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 18px;
  color: var(--rc-cream);
}

.nav-hub {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--rc-gold-soft);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-link {
  font-size: 14px;
  font-weight: 600;
  color: rgba(251, 247, 239, 0.65);
  text-decoration: none;
  transition: color 0.2s ease;
  cursor: pointer;
}

.nav-link:hover,
.nav-link.router-link-exact-active {
  color: var(--rc-gold-soft);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-btn-login.v-btn {
  border-color: rgba(251, 247, 239, 0.3) !important;
  color: var(--rc-cream) !important;
  font-weight: 600;
  font-size: 13.5px;
  border-radius: 8px;
}

.nav-btn-demo.v-btn {
  background: linear-gradient(150deg, var(--rc-gold-soft) 0%, var(--rc-gold) 100%) !important;
  color: var(--rc-teal-deep) !important;
  font-weight: 700;
  font-size: 13.5px;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(200, 155, 76, 0.3) !important;
}

.nav-burger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
}

@media (max-width: 960px) {
  .nav-links,
  .nav-actions {
    display: none;
  }
  .nav-burger {
    display: flex;
  }
}

/* Mobile drawer */
.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(20, 38, 35, 0.6);
  backdrop-filter: blur(4px);
}

.drawer-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: 300px;
  max-width: 85vw;
  height: 100%;
  background: var(--rc-cream, #FBF7EF);
  padding: 20px;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.2);
  font-family: 'Inter', sans-serif;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.drawer-logo {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: linear-gradient(150deg, #E4C989 0%, #C89B4C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer-brand {
  flex: 1;
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 16px;
  color: #26302C;
  line-height: 1.1;
}

.brand-sub {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: #8A6A2B;
}

.drawer-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #5B6663;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.drawer-link {
  display: flex;
  align-items: center;
  padding: 12px 10px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #26302C;
  cursor: pointer;
  text-decoration: none;
}

.drawer-link:hover {
  background: #F5F0E4;
}

.drawer-link-gold {
  background: linear-gradient(150deg, #E4C989 0%, #C89B4C 100%);
  color: #1E3733;
}
</style>